import { IStation } from 'utils/interfaces'
import {
  getStationFeedbackStatus,
  stationFeedbackSortRank,
  stationHasDiagnosticsError,
} from 'utils/stationFeedback'

/** Fleet / Assets list: online → no_feedback → offline; among online, diagnostics first; then id. */
export function sortStationsForAssetsList(stations: IStation[]): IStation[] {
  return [...stations].sort((a, b) => {
    const statusA = getStationFeedbackStatus(a)
    const statusB = getStationFeedbackStatus(b)
    const rankDiff = stationFeedbackSortRank(statusA) - stationFeedbackSortRank(statusB)
    if (rankDiff !== 0) {
      return rankDiff
    }

    // Same idea as robots: elevate diagnostics only among online assets.
    if (statusA === 'online') {
      const diagA = stationHasDiagnosticsError(a) ? 1 : 0
      const diagB = stationHasDiagnosticsError(b) ? 1 : 0
      if (diagA !== diagB) {
        return diagB - diagA
      }
    }

    return a.station_id.localeCompare(b.station_id)
  })
}

/**
 * Apply live telemetry snapshot fields that affect list sort
 * (online / feedback_status / diagnostics error).
 * Returns the same array reference when sort-related fields would not change.
 *
 * Also persists explicit `feedback_status` when live fills it in after a list
 * row that only had `online` (avoids undefined ↔ offline sort flicker).
 */
export function applyLiveStationSortFields(
  stations: IStation[],
  fieldsById: Record<number, Partial<IStation>>
): IStation[] {
  let changed = false
  const next = stations.map(station => {
    const patch = fieldsById[station.id]
    if (!patch) {
      return station
    }
    const merged = { ...station, ...patch }
    const feedbackUnchanged =
      getStationFeedbackStatus(merged) === getStationFeedbackStatus(station) &&
      merged.feedback_status === station.feedback_status &&
      Boolean(merged.online) === Boolean(station.online)
    const diagnosticsUnchanged =
      stationHasDiagnosticsError(merged) === stationHasDiagnosticsError(station) &&
      Number(merged.diagnostics_error_code ?? 0) === Number(station.diagnostics_error_code ?? 0)
    if (feedbackUnchanged && diagnosticsUnchanged) {
      return station
    }
    changed = true
    return merged
  })
  return changed ? next : stations
}

/**
 * Merge a fresh station list into previous state without wiping live sort fields.
 * Telemetry-snapshots are the source of truth for online / feedback / diagnostics;
 * list polls only refresh metadata (name, point, facilities, …).
 */
export function mergeStationsFromList(prev: IStation[], list: IStation[]): IStation[] {
  if (prev.length === 0) {
    return list
  }
  const prevById = new Map(prev.map(station => [station.id, station]))
  return list.map(station => {
    const existing = prevById.get(station.id)
    if (!existing) {
      return station
    }
    return {
      ...station,
      online: existing.online,
      feedback_status: existing.feedback_status ?? station.feedback_status,
      expects_telemetry: existing.expects_telemetry ?? station.expects_telemetry,
      diagnostics_error_code: existing.diagnostics_error_code,
      diagnostics_error_msg: existing.diagnostics_error_msg,
      inventory_mismatch: existing.inventory_mismatch ?? station.inventory_mismatch,
      lot_ids_only_in_db: existing.lot_ids_only_in_db ?? station.lot_ids_only_in_db,
      lot_ids_only_in_telemetry: existing.lot_ids_only_in_telemetry ?? station.lot_ids_only_in_telemetry,
      reported_lot_ids: existing.reported_lot_ids ?? station.reported_lot_ids,
      lot_model_mismatches: existing.lot_model_mismatches ?? station.lot_model_mismatches,
      lastTelemetryAt: existing.lastTelemetryAt ?? station.lastTelemetryAt,
    }
  })
}
